import MobileLinkPrompt from '@/components/MobileLinkPrompt';
import HiringPreferencePicker from '@/components/HiringPreferencePicker';
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import PolicyConsentChecklist, { emptyPolicyConsent, hasAcceptedAllPolicies } from '@/components/PolicyConsentChecklist';
import { useCollaboratorAuth } from '@/context/CollaboratorAuthContext';
import { collaboratorProfilePath } from '@/lib/collaboratorSlug';
import { hasLocalPolicyAcceptance, markLocalPolicyAcceptance, withAcceptedPolicyFields } from '@/lib/policyAcceptance';
import { analyzeCollaboratorProfile, buildCollaboratorBioSuggestions, profileScoreBadgeClass, profileScoreBarClass } from '@/lib/profileCoach';
import { getCollaboratorMatchSuggestions, getMatchConfidenceLabel } from '@/lib/smartMatch';
import { artistCategoryNames } from '@/lib/artistCategories';
import {
  defaultCollaboratorCategoryOptions,
  getCollaboratorSubcategoryOptions,
  inferCollaboratorMainCategory,
  mergeCollaboratorCategorySettings,
  toCollaboratorCategoryPayload,
} from '@/lib/collaboratorCategories';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_URL}/api`;
const MAX_PORTFOLIO_IMAGES = 3;
const MAX_PORTFOLIO_VIDEOS = 2;
const withFixedSlots = (values, count) => [...(Array.isArray(values) ? values : []), ...Array(count).fill('')].slice(0, count);
const collaboratorCategoryOptions = artistCategoryNames.filter((category) => category !== 'Other').slice(0, 36);
const splitLookingFor = (value = '') =>
  String(value)
    .split(',')
    .map((item) => item.trim())
    .filter(Boolean);
const readToggleValue = (value, defaultValue = true) => {
  if (value === undefined || value === null) return defaultValue;
  if (typeof value === 'string') return value.toLowerCase() !== 'false';
  return Boolean(value);
};

const extractYouTubeId = (url = '') => {
  const patterns = [
    /(?:youtube\.com\/watch\?.*v=|youtu\.be\/)([\w-]+)/,
    /youtube\.com\/embed\/([\w-]+)/,
    /youtube\.com\/v\/([\w-]+)/,
    /youtube\.com\/(?:shorts|live)\/([\w-]+)/,
  ];
  for (const pattern of patterns) {
    const match = String(url || '').match(pattern);
    if (match) return match[1];
  }
  return null;
};

const renderFormattedInlineText = (text) => {
  const parts = String(text || '').split(/(\*\*[^*]+\*\*)/g);
  return parts.map((part, index) => {
    if (part.startsWith('**') && part.endsWith('**') && part.length > 4) {
      return <strong key={`${part}-${index}`} className="font-extrabold text-slate-950">{part.slice(2, -2)}</strong>;
    }
    return <span key={`${part}-${index}`}>{part}</span>;
  });
};

const BIO_BULLET = String.fromCharCode(8226);
const BIO_BULLET_SPLIT_REGEX = /\s+(?:\u2022|\u00e2\u20ac\u00a2)\s+/g;
const BIO_BULLET_LINE_REGEX = /^(?:[-*]|\u2022|\u00e2\u20ac\u00a2)\s+/;

const normalizeCollaboratorBioSource = (source = '') => {
  const bulletLine = `\n${BIO_BULLET} `;
  const text = String(source || '')
    .replace(/\r/g, '')
    .replace(/(Services\s*&\s*Specializations\s*:)/gi, '\n### $1')
    .replace(/(Why\s+Choose[^?]+\?)/gi, '\n### $1')
    .replace(/(\s+)(Explore\s+[^.]+\s+on\s+KalaManch\.art)/gi, '\n$2')
    .replace(BIO_BULLET_SPLIT_REGEX, bulletLine)
    .trim();
  if (!text) return '';

  const rawLines = text.split('\n').map((line) => line.replace(/\s+/g, ' ').trim());
  const normalizedLines = [];
  let paragraphBuffer = [];

  const flushParagraph = () => {
    if (!paragraphBuffer.length) return;
    const paragraph = paragraphBuffer.join(' ').replace(/\s+/g, ' ').replace(/\s+([,.;:!?])/g, '$1').trim();
    if (paragraph) normalizedLines.push(paragraph);
    paragraphBuffer = [];
  };

  rawLines.forEach((line) => {
    if (!line) {
      flushParagraph();
      return;
    }
    if (/^#{1,6}\s+/.test(line) || BIO_BULLET_LINE_REGEX.test(line) || /^(\d+)[.)]\s+/.test(line)) {
      flushParagraph();
      normalizedLines.push(line);
      return;
    }
    paragraphBuffer.push(line);
  });

  flushParagraph();
  return normalizedLines.join('\n').trim();
};

const CollaboratorFormattedBio = ({ text, fallback }) => {
  const source = normalizeCollaboratorBioSource(text || fallback || '');
  if (!source) return null;

  return (
    <div className="space-y-3 text-base leading-8 text-gray-700" dir="auto">
      {source.split(/\r?\n/).map((rawLine, index) => {
        const line = rawLine.trim();
        if (!line) return null;

        const heading = line.match(/^#{1,6}\s+(.+)$/);
        if (heading) {
          return (
            <h4 key={`heading-${index}`} className="pt-3 text-lg font-extrabold text-slate-950">
              {renderFormattedInlineText(heading[1].replace(/:\s*$/, ''))}
            </h4>
          );
        }

        const bullet = line.match(/^(?:[-*]|\u2022|\u00e2\u20ac\u00a2)\s+(.+)$/);
        if (bullet) {
          return (
            <div key={`bullet-${index}`} className="flex gap-3">
              <span className="mt-3 h-2 w-2 shrink-0 rounded-full bg-[#FF6F00]" />
              <p className="min-w-0 flex-1">{renderFormattedInlineText(bullet[1])}</p>
            </div>
          );
        }

        return <p key={`line-${index}`}>{renderFormattedInlineText(line)}</p>;
      })}
    </div>
  );
};
const projectStatusLabel = {
  artist_accepted: 'Artist interested',
  artist_declined: 'Declined',
  in_review: 'Under review',
  poster_accepted: 'Selected',
  poster_rejected: 'Not selected',
  pending_review: 'Admin review',
  published: 'Published',
  admin_rejected: 'Needs changes',
  closed: 'Closed',
};

const castingStatusLabel = {
  open: 'Draft brief',
  matched: 'Matches ready',
};

const PasswordToggleButton = ({ visible, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    className="absolute inset-y-0 right-3 flex items-center text-gray-500 hover:text-gray-700"
    aria-label={visible ? 'Hide password' : 'Show password'}
    title={visible ? 'Hide password' : 'Show password'}
  >
    {visible ? (
      <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.8" d="M3 3l18 18" />
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.8" d="M10.58 10.58A2 2 0 0012 14a2 2 0 001.42-.58" />
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.8" d="M9.88 5.09A10.94 10.94 0 0112 4.91c5.05 0 8.27 4.14 9 5.09a13.16 13.16 0 01-3.06 3.37M6.1 6.1A13.23 13.23 0 003 12c.73.95 3.95 5.09 9 5.09a10.9 10.9 0 004.11-.79" />
      </svg>
    ) : (
      <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.8" d="M2.46 12C3.73 7.94 7.52 5 12 5c4.48 0 8.27 2.94 9.54 7-1.27 4.06-5.06 7-9.54 7-4.48 0-8.27-2.94-9.54-7z" />
        <circle cx="12" cy="12" r="3" strokeWidth="1.8" />
      </svg>
    )}
  </button>
);

const CollaboratorAccount = () => {
  const { collaborator, setCollaborator, updateCollaborator, logout, changePassword } = useCollaboratorAuth();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    organization_name: '',
    organization_type: '',
    organization_category: 'Production & Talent Services',
    organization_subcategories: [],
    organization_subcategory_other: '',
    city: '',
    website: '',
    instagram: '',
    facebook: '',
    bio: '',
    looking_for_text: '',
    image_url: '',
    portfolio_image_urls: ['', '', ''],
    portfolio_video_urls: ['', ''],
    allow_artist_enquiries: true,
    allow_collaborator_requests: true,
  });
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });
  const [passwordSaving, setPasswordSaving] = useState(false);
  const [passwordMessage, setPasswordMessage] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [passwordVisibility, setPasswordVisibility] = useState({
    currentPassword: false,
    newPassword: false,
    confirmPassword: false,
  });
  const [policyConsent, setPolicyConsent] = useState(emptyPolicyConsent);
  const [policySaving, setPolicySaving] = useState(false);
  const [policyError, setPolicyError] = useState('');
  const [projectSummary, setProjectSummary] = useState({ projects: [], responses: [] });
  const [castingSummary, setCastingSummary] = useState([]);
  const [collaboratorCategoryMap, setCollaboratorCategoryMap] = useState(defaultCollaboratorCategoryOptions);

  useEffect(() => {
    let mounted = true;
    const loadCategories = async () => {
      try {
        const response = await axios.get(`${API}/settings/collaborator-categories`);
        if (mounted) {
          setCollaboratorCategoryMap(mergeCollaboratorCategorySettings(response.data?.settings));
        }
      } catch (error) {
        if (mounted) setCollaboratorCategoryMap(defaultCollaboratorCategoryOptions);
      }
    };
    loadCategories();
    return () => {
      mounted = false;
    };
  }, []);

  useEffect(() => {
    if (!collaborator) return;
    setFormData({
      name: collaborator.name || '',
      email: collaborator.email || '',
      organization_name: collaborator.organization_name || '',
      organization_type: collaborator.organization_type || '',
      organization_category:
        collaborator.organization_category ||
        inferCollaboratorMainCategory(
          collaboratorCategoryMap,
          Array.isArray(collaborator.organization_subcategories) && collaborator.organization_subcategories.length
            ? collaborator.organization_subcategories
            : splitLookingFor(collaborator.organization_type || '')
        ) ||
        'Production & Talent Services',
      organization_subcategories: Array.isArray(collaborator.organization_subcategories) && collaborator.organization_subcategories.length
        ? collaborator.organization_subcategories
        : splitLookingFor(collaborator.organization_type || ''),
      organization_subcategory_other: collaborator.organization_subcategory_other || '',
      city: collaborator.city || '',
      website: collaborator.website || '',
      instagram: collaborator.instagram || '',
      facebook: collaborator.facebook || '',
      bio: collaborator.bio || '',
      looking_for_text: Array.isArray(collaborator.looking_for) ? collaborator.looking_for.join(', ') : '',
      image_url: collaborator.image_url || '',
      portfolio_image_urls: withFixedSlots(collaborator.portfolio_image_urls, MAX_PORTFOLIO_IMAGES),
      portfolio_video_urls: withFixedSlots(collaborator.portfolio_video_urls, MAX_PORTFOLIO_VIDEOS),
      allow_artist_enquiries: readToggleValue(collaborator.allow_artist_enquiries, true),
      allow_collaborator_requests: readToggleValue(collaborator.allow_collaborator_requests, true),
    });
  }, [collaborator, collaboratorCategoryMap]);

  useEffect(() => {
    if (!collaborator?.id) return;
    let mounted = true;
    const fetchProjects = async () => {
      try {
        const response = await axios.get(`${API}/projects/mine`, { withCredentials: true });
        if (mounted) {
          setProjectSummary({
            projects: response.data.projects || [],
            responses: response.data.responses || [],
          });
        }
      } catch (fetchError) {
        if (mounted) setProjectSummary({ projects: [], responses: [] });
      }
    };
    fetchProjects();
    return () => {
      mounted = false;
    };
  }, [collaborator?.id]);

  useEffect(() => {
    if (!collaborator?.id) return;
    let mounted = true;
    const fetchCastingSummary = async () => {
      try {
        const response = await axios.get(`${API}/casting/client/${collaborator.id}/briefs`, { withCredentials: true });
        if (mounted) {
          setCastingSummary(response.data?.briefs || []);
        }
      } catch (fetchError) {
        if (mounted) setCastingSummary([]);
      }
    };
    fetchCastingSummary();
    return () => {
      mounted = false;
    };
  }, [collaborator?.id]);

  if (!collaborator) return null;

  const handleChange = (event) => {
    const { name, value } = event.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleOrganizationCategoryChange = (value) => {
    const firstSubcategory = getCollaboratorSubcategoryOptions(collaboratorCategoryMap, value)[0] || '';
    setFormData((prev) => ({
      ...prev,
      organization_category: value,
      organization_subcategories: firstSubcategory ? [firstSubcategory] : [],
      organization_subcategory_other: '',
      organization_type: firstSubcategory || value,
    }));
  };

  const toggleOrganizationSubcategory = (subcategory) => {
    setFormData((prev) => {
      const selected = Array.isArray(prev.organization_subcategories) ? prev.organization_subcategories : [];
      const exists = selected.some((item) => item.toLowerCase() === subcategory.toLowerCase());
      const nextSubcategories = exists
        ? selected.filter((item) => item.toLowerCase() !== subcategory.toLowerCase())
        : [...selected, subcategory];
      return {
        ...prev,
        organization_subcategories: nextSubcategories,
        organization_type: toCollaboratorCategoryPayload({
          ...prev,
          organization_subcategories: nextSubcategories,
        }).organization_type,
      };
    });
  };

  const toggleLookingForCategory = (category) => {
    setFormData((prev) => {
      const current = splitLookingFor(prev.looking_for_text);
      const exists = current.some((item) => item.toLowerCase() === category.toLowerCase());
      const next = exists
        ? current.filter((item) => item.toLowerCase() !== category.toLowerCase())
        : [...current, category];
      return { ...prev, looking_for_text: next.join(', ') };
    });
  };

  const handleSlotChange = (field, index, value) => {
    setFormData((prev) => {
      const limit = field === 'portfolio_image_urls' ? MAX_PORTFOLIO_IMAGES : MAX_PORTFOLIO_VIDEOS;
      const nextValues = [...withFixedSlots(prev[field], limit)];
      nextValues[index] = value;
      return { ...prev, [field]: nextValues };
    });
  };

  const handleImageUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    setUploadingImage(true);
    setError('');
    try {
      const payload = new FormData();
      payload.append('file', file);
      const response = await axios.post(`${API}/uploads/profile-image`, payload, {
        headers: { 'Content-Type': 'multipart/form-data' },
        withCredentials: true,
      });
      setFormData((prev) => ({ ...prev, image_url: response.data.image_url }));
      setMessage('Profile image uploaded. Save changes to publish it.');
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to upload image.');
    } finally {
      setUploadingImage(false);
      event.target.value = '';
    }
  };

  const handlePortfolioImageUpload = async (event, index) => {
    const file = event.target.files?.[0];
    if (!file) return;
    setUploadingImage(true);
    setError('');
    try {
      const payload = new FormData();
      payload.append('file', file);
      const response = await axios.post(`${API}/uploads/profile-image`, payload, {
        headers: { 'Content-Type': 'multipart/form-data' },
        withCredentials: true,
      });
      handleSlotChange('portfolio_image_urls', index, response.data.image_url || '');
      setMessage('Portfolio photo uploaded. Save changes to publish it.');
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to upload image.');
    } finally {
      setUploadingImage(false);
      event.target.value = '';
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setSaving(true);
    setError('');
    setMessage('');
    try {
      await updateCollaborator({
        ...formData,
        ...toCollaboratorCategoryPayload(formData),
        looking_for: formData.looking_for_text.split(',').map((value) => value.trim()).filter(Boolean),
        portfolio_video_urls: formData.portfolio_video_urls.map((value) => value.trim()).filter(Boolean),
      });
      setMessage('Collaborator profile updated.');
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to save collaborator profile.');
    } finally {
      setSaving(false);
    }
  };

  const handlePasswordChange = async (event) => {
    event.preventDefault();
    setPasswordMessage('');
    setPasswordError('');

    if (passwordData.newPassword.trim() !== passwordData.confirmPassword.trim()) {
      setPasswordError('New password and confirmation do not match.');
      return;
    }
    if (passwordData.newPassword.trim().length < 8) {
      setPasswordError('New password must be at least 8 characters.');
      return;
    }

    setPasswordSaving(true);
    try {
      const response = await changePassword({
        currentPassword: passwordData.currentPassword,
        newPassword: passwordData.newPassword,
      });
      setPasswordMessage(response?.message || 'Password changed successfully. A confirmation email has been sent.');
      setPasswordData({ currentPassword: '', newPassword: '', confirmPassword: '' });
    } catch (saveError) {
      setPasswordError(saveError.response?.data?.detail || saveError.message || 'Failed to change password.');
    } finally {
      setPasswordSaving(false);
    }
  };

  const togglePasswordVisibility = (field) => {
    setPasswordVisibility((prev) => ({ ...prev, [field]: !prev[field] }));
  };

  const updatePolicyConsent = (key, value) => {
    setPolicyConsent((prev) => ({ ...prev, [key]: value }));
    setPolicyError('');
  };

  const publicProfileUrl = collaboratorProfilePath(collaborator);
  const collaboratorCategoryNames = Object.keys(collaboratorCategoryMap || {});
  const organizationSubcategoryOptions = getCollaboratorSubcategoryOptions(
    collaboratorCategoryMap,
    formData.organization_category
  );
  const publicContactSummary = [
    formData.allow_artist_enquiries ? 'Artists can contact this profile' : 'Artist enquiries are turned off',
    formData.allow_collaborator_requests ? 'Collaborators can view contact after login' : 'Collaborator-to-collaborator contact is turned off',
  ];
  const publicPortfolioImages = formData.portfolio_image_urls.map((value) => value.trim()).filter(Boolean);
  const publicPortfolioVideos = formData.portfolio_video_urls.map((value) => value.trim()).filter(Boolean);
  const needsPolicyAcceptance = !hasLocalPolicyAcceptance('collaborator', collaborator) && !hasAcceptedAllPolicies({
    privacy: collaborator.privacy_policy_accepted,
    terms: collaborator.terms_accepted,
    trust: collaborator.trust_safety_accepted,
  });

  const handlePolicyAcceptance = async () => {
    if (!hasAcceptedAllPolicies(policyConsent)) {
      setPolicyError('Please read and accept all three policies before continuing.');
      return;
    }
    setPolicySaving(true);
    setPolicyError('');
    try {
      const response = await axios.post(
        `${API}/collaborator-auth/policy-acceptance`,
        {
          privacy_policy_accepted: policyConsent.privacy,
          terms_accepted: policyConsent.terms,
          trust_safety_accepted: policyConsent.trust,
        },
        { withCredentials: true }
      );
      const acceptedCollaborator = withAcceptedPolicyFields(response.data);
      markLocalPolicyAcceptance('collaborator', acceptedCollaborator);
      setCollaborator(acceptedCollaborator);
      setMessage('Policy acceptance saved. Admin verification still controls public publishing.');
    } catch (requestError) {
      setPolicyError(requestError.response?.data?.detail || 'Unable to save policy acceptance.');
    } finally {
      setPolicySaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 pt-24 pb-16">
      <MobileLinkPrompt account={collaborator} accountType="collaborator" />
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="bg-white rounded-3xl shadow-xl p-5 sm:p-8 mb-8">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6">
            <div className="flex min-w-0 items-center gap-4 sm:gap-5">
              <div className="w-20 h-20 sm:w-24 sm:h-24 rounded-full overflow-hidden bg-white border border-orange-100 flex items-center justify-center text-3xl sm:text-4xl font-bold text-[#00BFA5] shrink-0">
                {collaborator.image_url ? (
                  <img src={collaborator.image_url} alt={collaborator.organization_name} className="w-full h-full object-contain p-1" />
                ) : (
                  (collaborator.organization_name || collaborator.name || 'C').charAt(0).toUpperCase()
                )}
              </div>
              <div className="min-w-0">
                <h1 className="text-3xl sm:text-4xl font-bold gradient-text mb-2">Collaborator Account</h1>
                <p className="text-gray-700 text-base sm:text-lg break-words">{collaborator.organization_name}</p>
                <p className="text-sm text-gray-500 break-words">{collaborator.organization_type}</p>
              </div>
            </div>
            <div className="grid grid-cols-1 sm:flex sm:flex-wrap gap-3">
              <Link to={publicProfileUrl} className="btn-outline text-center">View Public Profile</Link>
              <Link to="/collaborator/inbox" className="btn-outline text-center">Open Inbox</Link>
              <Link to="/projects" className="btn-outline text-center">Projects</Link>
              <Link to="/casting/post" className="btn-outline text-center">AI Casting Director</Link>
              <button type="button" className="btn-primary" onClick={logout}>Logout</button>
            </div>
          </div>
        </div>

        {needsPolicyAcceptance && (
          <div className="mb-8 rounded-3xl border border-teal-200 bg-white p-5 shadow-lg sm:p-6">
            <p className="text-xs font-black uppercase tracking-[0.22em] text-[#00BFA5]">Account acceptance required</p>
            <h2 className="mt-2 text-2xl font-black text-gray-950">Accept current platform policies</h2>
            <p className="mt-2 text-sm leading-6 text-gray-700">
              This account can be reviewed by admin only after the Privacy Policy, Terms & Conditions, and Trust & Safety rules are accepted. Admin verification still controls public publishing.
            </p>
            <div className="mt-4">
              <PolicyConsentChecklist value={policyConsent} onChange={updatePolicyConsent} tone="teal" />
            </div>
            {policyError && <p className="mt-3 text-sm font-semibold text-red-600">{policyError}</p>}
            <button
              type="button"
              className="btn-primary mt-4"
              onClick={handlePolicyAcceptance}
              disabled={policySaving || !hasAcceptedAllPolicies(policyConsent)}
            >
              {policySaving ? 'Saving...' : 'Accept and Continue'}
            </button>
          </div>
        )}

          <div className="mb-8 grid gap-4 md:grid-cols-2">
            <Link to="/projects" className="rounded-3xl border border-teal-100 bg-white p-5 shadow-lg transition hover:-translate-y-0.5 hover:shadow-xl">
              <p className="text-xs font-black uppercase tracking-[0.22em] text-[#00BFA5]">Projects</p>
              <h2 className="mt-2 text-2xl font-black text-gray-950">Posted project dashboard</h2>
              <p className="mt-2 text-sm text-gray-600">
                {projectSummary.projects.length} posted project(s), {projectSummary.responses.length} artist response(s).
              </p>
            </Link>
            {castingSummary.length > 0 ? (
              <div className="rounded-3xl border border-purple-100 bg-white p-5 shadow-lg">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <p className="text-xs font-black uppercase tracking-[0.22em] text-[#4A148C]">AI Casting Director</p>
                    <h2 className="mt-2 text-2xl font-black text-gray-950">Recent casting briefs</h2>
                    <p className="mt-2 text-sm text-gray-600">
                      {castingSummary.length} brief(s) posted through KalaManch AI matching.
                    </p>
                  </div>
                  <Link to="/casting/post" className="btn-outline text-center">New brief</Link>
                </div>
                <div className="mt-3 space-y-2">
                  {castingSummary.slice(0, 3).map((brief) => (
                    <div key={brief.id} className="rounded-2xl bg-purple-50 px-4 py-3">
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0">
                          <p className="truncate text-sm font-black text-gray-950">
                            {brief.parsed?.occasion_type || brief.parsed?.art_form || 'Creative brief'}
                          </p>
                          <p className="mt-1 text-xs font-bold text-[#4A148C]">
                            {castingStatusLabel[brief.status] || brief.status} • {brief.match_count || 0} matches • {brief.shortlisted_count || 0} shortlisted
                          </p>
                        </div>
                        <Link to={`/casting/results/${brief.id}`} className="shrink-0 rounded-full border border-[#4A148C]/20 bg-white px-3 py-1 text-xs font-bold text-[#4A148C]">
                          Open
                        </Link>
                      </div>
                      <p className="mt-2 line-clamp-2 text-xs text-gray-600">{brief.raw_text}</p>
                    </div>
                  ))}
                </div>
              </div>
            ) : (
            <Link to="/casting/post" className="rounded-3xl border border-orange-100 bg-white p-5 shadow-lg transition hover:-translate-y-0.5 hover:shadow-xl">
              <p className="text-xs font-black uppercase tracking-[0.22em] text-[#FF6F00]">AI Casting Director</p>
              <h2 className="mt-2 text-2xl font-black text-gray-950">Post a work brief and get artist matches</h2>
              <p className="mt-2 text-sm text-gray-600">
                Let Kalamanch rank suitable artists for your event, campaign, workshop, or live requirement.
              </p>
            </Link>
          )}
        </div>

        <div className="bg-white rounded-3xl shadow-lg p-4 sm:p-8 mb-8">
          <p className="text-xs font-semibold tracking-[0.14em] sm:tracking-[0.28em] text-[#00BFA5] uppercase mb-4 break-words">Public Profile View</p>
          <div className="rounded-3xl border border-teal-100 bg-white overflow-hidden">
            <div className="bg-gradient-to-br from-[#0E8B73] via-[#4A148C] to-[#B35A12] px-3 py-5 sm:px-8 sm:py-8">
              <div className="rounded-3xl bg-white shadow-xl p-4 sm:p-8">
                <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-6">
                  <div className="flex min-w-0 flex-col sm:flex-row sm:items-start gap-4 sm:gap-5">
                    <div className="w-20 h-20 sm:w-24 sm:h-24 rounded-full overflow-hidden bg-white border border-orange-100 flex items-center justify-center text-3xl sm:text-4xl font-bold text-[#00BFA5] shrink-0">
                      {collaborator.image_url ? (
                        <img src={collaborator.image_url} alt={collaborator.organization_name} className="w-full h-full object-contain p-1" />
                      ) : (
                        (collaborator.organization_name || collaborator.name || 'C').charAt(0).toUpperCase()
                      )}
                    </div>
                    <div className="min-w-0">
                      <h2 className="text-2xl sm:text-4xl font-bold text-gray-900 break-words">{collaborator.organization_name || collaborator.name || 'Collaborator profile'}</h2>
                      <p className="mt-2 text-lg sm:text-xl font-semibold text-[#00BFA5] break-words">{collaborator.organization_type || 'Collaborator account'}</p>
                      {collaborator.city && <p className="mt-2 text-gray-600 break-words">{collaborator.city}</p>}
                      <div className="mt-4 flex flex-wrap gap-2">
                        <span className={`rounded-full border px-3 py-1 text-sm font-semibold ${collaborator.is_verified ? 'border-green-200 bg-green-50 text-green-800' : 'border-orange-200 bg-orange-50 text-orange-800'}`}>
                          {collaborator.is_verified ? 'Verified and visible' : 'Awaiting review'}
                        </span>
                        {Array.isArray(collaborator.looking_for) && collaborator.looking_for.length > 0 && (
                          <span className="max-w-full break-words rounded-full border border-teal-100 bg-teal-50 px-3 py-1 text-sm text-gray-700">
                            Looking for: {collaborator.looking_for.slice(0, 2).join(', ')}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="w-full lg:w-auto flex flex-col sm:flex-row lg:flex-col gap-3">
                    <Link to={publicProfileUrl} className="btn-primary text-center">View Public Profile</Link>
                    <a href="#edit-collaborator-profile" className="btn-outline text-center">Edit Public Details</a>
                  </div>
                </div>
              </div>
            </div>

            <div className="mt-0 grid grid-cols-1 lg:grid-cols-3 gap-5 sm:gap-6 p-4 sm:p-8">
              <div className="lg:col-span-2 rounded-3xl border border-teal-100 bg-teal-50/40 p-5">
                <h3 className="text-xl font-bold text-gray-900 mb-3">About</h3>
                <CollaboratorFormattedBio text={formData.bio || collaborator.bio} fallback="Your public collaborator note will appear here once you explain what kind of artists, events, or projects you handle." />
              </div>
              <div className="rounded-3xl border border-teal-100 bg-white p-5">
                <h3 className="text-xl font-bold text-gray-900 mb-3">Contact View</h3>
                <div className="space-y-2 text-sm text-gray-700">
                  {publicContactSummary.map((item) => (
                    <p key={item}>{item}</p>
                  ))}
                  <p><span className="font-semibold">Email:</span> {collaborator.email || 'Not added'}</p>
                </div>
              </div>
              <div className="rounded-3xl border border-teal-100 bg-white p-5">
                <h3 className="text-xl font-bold text-gray-900 mb-3">Looking For</h3>
                <div className="flex flex-wrap gap-2">
                  {(Array.isArray(collaborator.looking_for) && collaborator.looking_for.length ? collaborator.looking_for : ['Add preferences in edit profile']).map((item) => (
                    <span key={item} className="rounded-full bg-teal-50 px-3 py-1 text-sm text-gray-700 border border-teal-100">
                      {item}
                    </span>
                  ))}
                </div>
              </div>
              {(publicPortfolioImages.length > 0 || publicPortfolioVideos.length > 0) && (
                <div className="lg:col-span-3 rounded-3xl border border-teal-100 bg-white p-5">
                  <h3 className="text-xl font-bold text-gray-900 mb-4">Portfolio</h3>
                  {publicPortfolioImages.length > 0 && (
                    <div>
                      <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#FF6F00] mb-3">Photos</p>
                      <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                        {publicPortfolioImages.map((imageUrl, index) => (
                          <a
                            key={`${imageUrl}-${index}`}
                            href={imageUrl}
                            target="_blank"
                            rel="noreferrer"
                            className="group overflow-hidden rounded-2xl border border-orange-100 bg-gray-50"
                          >
                            <img
                              src={imageUrl}
                              alt={`${collaborator.organization_name || collaborator.name || 'Collaborator'} portfolio ${index + 1}`}
                              className="h-40 w-full bg-white object-contain transition-transform duration-200 group-hover:scale-[1.02]"
                            />
                          </a>
                        ))}
                      </div>
                    </div>
                  )}
                  {publicPortfolioVideos.length > 0 && (
                    <div className={publicPortfolioImages.length > 0 ? 'mt-6' : ''}>
                      <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#FF6F00] mb-3">Videos</p>
                      <div className="grid gap-5 md:grid-cols-2">
                        {publicPortfolioVideos.map((videoUrl, index) => {
                          const youtubeId = extractYouTubeId(videoUrl);
                          return (
                            <div key={`${videoUrl}-${index}`} className="overflow-hidden rounded-2xl border border-purple-100 bg-white shadow-md">
                              {youtubeId ? (
                                <div className="aspect-video bg-slate-950">
                                  <iframe
                                    src={`https://www.youtube.com/embed/${youtubeId}`}
                                    title={`${collaborator.organization_name || collaborator.name || 'Collaborator'} portfolio video ${index + 1}`}
                                    className="h-full w-full"
                                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                                    allowFullScreen
                                  />
                                </div>
                              ) : (
                                <a
                                  href={videoUrl}
                                  target="_blank"
                                  rel="noreferrer"
                                  className="flex aspect-video items-center justify-center bg-purple-50 px-4 text-center font-black text-[#4A148C]"
                                >
                                  Open portfolio video
                                </a>
                              )}
                              <div className="px-4 py-3">
                                <p className="font-bold text-slate-950">{collaborator.organization_name || collaborator.name || 'Collaborator'} portfolio video {index + 1}</p>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 xl:grid-cols-3 gap-8">
          <div className="xl:col-span-2">
            <form id="edit-collaborator-profile" onSubmit={handleSubmit} className="bg-white rounded-3xl shadow-lg p-8">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-3xl font-bold gradient-text">Edit Profile</h2>
                <span className="text-sm text-gray-500">Logged in as {collaborator.email || collaborator.mobile || 'your account'}</span>
              </div>
              {message && <div className="mb-4 rounded-xl bg-green-50 border border-green-200 px-4 py-3 text-green-700">{message}</div>}
              {error && <div className="mb-4 rounded-xl bg-red-50 border border-red-200 px-4 py-3 text-red-700">{error}</div>}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <input name="name" value={formData.name} onChange={handleChange} required placeholder="Contact Person" className="w-full px-4 py-3 border border-gray-300 rounded-xl" />
                <input name="email" type="email" value={formData.email} onChange={handleChange} required placeholder="Email" className="w-full px-4 py-3 border border-gray-300 rounded-xl" />
                <input name="organization_name" value={formData.organization_name} onChange={handleChange} required placeholder="Organization Name" className="w-full px-4 py-3 border border-gray-300 rounded-xl" />
                <select
                  name="organization_category"
                  value={formData.organization_category}
                  onChange={(event) => handleOrganizationCategoryChange(event.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl"
                >
                  {collaboratorCategoryNames.map((category) => (
                    <option key={category} value={category}>
                      {category}
                    </option>
                  ))}
                </select>
                <input name="city" value={formData.city} onChange={handleChange} placeholder="City" className="w-full px-4 py-3 border border-gray-300 rounded-xl" />
                <input name="website" value={formData.website} onChange={handleChange} placeholder="Website" className="w-full px-4 py-3 border border-gray-300 rounded-xl" />
                <input name="instagram" value={formData.instagram} onChange={handleChange} placeholder="Instagram profile URL" className="w-full px-4 py-3 border border-gray-300 rounded-xl" />
                <input name="facebook" value={formData.facebook} onChange={handleChange} placeholder="Facebook page URL" className="w-full px-4 py-3 border border-gray-300 rounded-xl" />
                <input
                  name="organization_subcategory_other"
                  value={formData.organization_subcategory_other}
                  onChange={handleChange}
                  placeholder="Other sub-category (optional)"
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl"
                />
                <input
                  name="looking_for_text"
                  value={formData.looking_for_text}
                  onChange={handleChange}
                  placeholder="Looking for (comma separated, for example: Singers, Dancers, Anchors)"
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl md:col-span-2"
                />
                {organizationSubcategoryOptions.length > 0 && (
                  <div className="md:col-span-2 rounded-2xl border border-purple-100 bg-purple-50/40 p-4">
                    <p className="text-sm font-bold text-gray-900">Select collaborator sub-categories</p>
                    <div className="mt-3 flex flex-wrap gap-2">
                      {organizationSubcategoryOptions.map((subcategory) => {
                        const selected = (formData.organization_subcategories || []).includes(subcategory);
                        return (
                          <button
                            key={subcategory}
                            type="button"
                            onClick={() => toggleOrganizationSubcategory(subcategory)}
                            className={`rounded-full border px-3 py-1.5 text-xs font-bold transition ${
                              selected
                                ? 'border-[#4A148C] bg-[#4A148C] text-white shadow'
                                : 'border-purple-100 bg-white text-gray-700 hover:border-[#4A148C]'
                            }`}
                          >
                            {subcategory}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}
                <HiringPreferencePicker value={formData.looking_for_text} onChange={value => setFormData(prev => ({ ...prev, looking_for_text: value }))} />
                <div className="md:col-span-2 rounded-2xl border border-teal-100 bg-teal-50/60 p-4">
                  <p className="font-semibold text-gray-900">Contact preferences</p>
                  <div className="mt-3 space-y-3">
                    <label className="flex items-start gap-3 text-sm text-gray-700">
                      <input
                        type="checkbox"
                        checked={Boolean(formData.allow_artist_enquiries)}
                        onChange={(event) => setFormData((prev) => ({ ...prev, allow_artist_enquiries: event.target.checked }))}
                        className="mt-1 h-4 w-4 rounded border-gray-300 text-[#00BFA5] focus:ring-[#00BFA5]"
                      />
                      <span>Allow artists to contact this collaborator profile through KalaManch.</span>
                    </label>
                    <label className="flex items-start gap-3 text-sm text-gray-700">
                      <input
                        type="checkbox"
                        checked={Boolean(formData.allow_collaborator_requests)}
                        onChange={(event) => setFormData((prev) => ({ ...prev, allow_collaborator_requests: event.target.checked }))}
                        className="mt-1 h-4 w-4 rounded border-gray-300 text-[#00BFA5] focus:ring-[#00BFA5]"
                      />
                      <span>Allow other collaborators to view contact details after login.</span>
                    </label>
                  </div>
                </div>
                <div className="md:col-span-2">
                  <input type="file" accept="image/*" onChange={handleImageUpload} className="w-full px-4 py-3 border border-gray-300 rounded-xl bg-white" />
                  {uploadingImage && <p className="text-sm text-[#00BFA5] mt-2">Uploading image...</p>}
                </div>
                <div className="md:col-span-2 rounded-xl border bg-gray-50 p-4"><p className="font-semibold">Public photo gallery</p><p className="mt-1 text-sm text-gray-600">Additional profile photos are managed by KalaManch admin and shown on your public profile. You can still change your main profile photo.</p></div>
                <div className="md:col-span-2 rounded-2xl border border-purple-100 bg-purple-50/60 p-4">
                  <p className="font-semibold text-gray-900">Portfolio videos</p>
                  <p className="mt-1 text-xs text-gray-500">Add up to {MAX_PORTFOLIO_VIDEOS} YouTube links only.</p>
                  <div className="mt-4 grid gap-3">
                    {formData.portfolio_video_urls.map((url, index) => (
                      <input key={`collab-portfolio-video-${index}`} value={url} onChange={(event) => handleSlotChange('portfolio_video_urls', index, event.target.value)} placeholder={`YouTube video ${index + 1} URL`} className="w-full px-4 py-3 border border-gray-300 rounded-xl bg-white" />
                    ))}
                  </div>
                </div>
                <textarea name="bio" value={formData.bio} onChange={handleChange} rows="5" placeholder="Describe the work, events, or collaborations you are looking for." className="w-full px-4 py-3 border border-gray-300 rounded-xl md:col-span-2" />
                <div className="md:col-span-2 rounded-3xl border border-teal-100 bg-gradient-to-br from-teal-50 via-white to-purple-50 p-4">
                  <p className="text-sm font-bold text-teal-900">AI Bio Helper</p>
                  <p className="mt-1 text-xs text-gray-600">Use a draft that explains what your organization looks for, then adjust it before saving.</p>
                  <div className="mt-3 grid gap-3">
                    {buildCollaboratorBioSuggestions(formData).map((suggestion) => (
                      <div key={suggestion.label} className="rounded-2xl bg-white/85 p-3 shadow-sm">
                        <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                          <p className="text-sm font-semibold text-gray-900">{suggestion.label}</p>
                          <button
                            type="button"
                            onClick={() => setFormData((prev) => ({ ...prev, bio: suggestion.text }))}
                            className="rounded-full border border-teal-200 px-3 py-1 text-xs font-bold text-teal-700 hover:bg-teal-50"
                          >
                            Use this bio
                          </button>
                        </div>
                        <p className="mt-2 text-sm leading-relaxed text-gray-600">{suggestion.text}</p>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
              <div className="mt-6">
                <button type="submit" className="btn-primary" disabled={saving}>{saving ? 'Saving...' : 'Save Changes'}</button>
              </div>
            </form>
          </div>
          <div className="space-y-8">
            <div className="bg-white rounded-3xl shadow-lg p-8">
              {(() => {
                const profileCoach = analyzeCollaboratorProfile({ ...collaborator, ...formData });
                const matchSuggestions = getCollaboratorMatchSuggestions({ ...collaborator, ...formData });
                return (
                  <>
                    <div className="flex items-start justify-between gap-4 mb-5">
                      <div>
                        <h2 className="text-2xl font-bold gradient-text">Profile Coach</h2>
                        <p className="mt-1 text-sm text-gray-500">Improve trust before contacting artists.</p>
                      </div>
                      <span className={`rounded-full border px-3 py-1 text-xs font-bold ${profileScoreBadgeClass(profileCoach.tone)}`}>
                        {profileCoach.label}
                      </span>
                    </div>
                    <div className="rounded-3xl border border-teal-100 bg-gradient-to-br from-teal-50 via-white to-purple-50 p-5">
                      <p className="text-sm font-semibold text-gray-500">Profile Strength</p>
                      <p className="mt-1 text-4xl font-bold text-gray-900">{profileCoach.score}<span className="text-lg text-gray-500">/100</span></p>
                      <div className="mt-4 h-3 overflow-hidden rounded-full bg-white shadow-inner">
                        <div
                          className={`h-full rounded-full bg-gradient-to-r ${profileScoreBarClass(profileCoach.tone)}`}
                          style={{ width: `${profileCoach.score}%` }}
                        />
                      </div>
                    </div>
                    {profileCoach.suggestions.length > 0 && (
                      <div className="mt-5">
                        <p className="text-sm font-bold text-gray-800">Next best improvements</p>
                        <div className="mt-2 space-y-2">
                          {profileCoach.suggestions.map((suggestion) => (
                            <p key={suggestion} className="rounded-2xl bg-gray-50 px-3 py-2 text-sm text-gray-700">
                              {suggestion}
                            </p>
                          ))}
                        </div>
                      </div>
                    )}
                    <div className="mt-6 rounded-3xl border border-orange-100 bg-orange-50/70 p-5">
                      <div className="flex items-center justify-between gap-3">
                        <div>
                          <p className="text-sm font-bold text-gray-900">Suggested artists</p>
                          <p className="mt-1 text-xs text-gray-600">{getMatchConfidenceLabel(matchSuggestions)}</p>
                        </div>
                      </div>
                      <div className="mt-3 flex flex-wrap gap-2">
                        {matchSuggestions.map((match) => (
                          <span key={match} className="rounded-full bg-white px-3 py-1 text-xs font-bold text-gray-700">
                            {match}
                          </span>
                        ))}
                      </div>
                    </div>
                  </>
                );
              })()}
            </div>
            <div className="bg-white rounded-3xl shadow-lg p-8">
              <h2 className="text-2xl font-bold gradient-text mb-4">Account Snapshot</h2>
              <div className="space-y-3 text-sm text-gray-700">
                <p><span className="font-semibold">Collaborator ID:</span> {collaborator.id}</p>
                <p><span className="font-semibold">Status:</span> {collaborator.is_verified ? 'Verified and visible' : 'Awaiting review'}</p>
                <p><span className="font-semibold">Organization:</span> {collaborator.organization_type}</p>
              </div>
            </div>
            <div className="bg-white rounded-3xl shadow-lg p-8">
              <h2 className="text-2xl font-bold gradient-text mb-4">Next Step</h2>
              <p className="text-sm text-gray-600 mb-4">Open your inbox to manage artist enquiries and keep conversations on the platform.</p>
              <Link to="/collaborator/inbox" className="btn-primary w-full text-center block">Go to Inbox</Link>
            </div>
            <div className="bg-white rounded-3xl shadow-lg p-8">
              <h2 className="text-2xl font-bold gradient-text mb-4">Change Password</h2>
              <p className="text-sm text-gray-600 mb-2">Update your KalaManch password.</p>
              <p className="text-xs text-gray-500 mb-4">New password must be at least 8 characters.</p>
              {passwordMessage && <div className="mb-4 rounded-xl bg-green-50 border border-green-200 px-4 py-3 text-green-700">{passwordMessage}</div>}
              {passwordError && <div className="mb-4 rounded-xl bg-red-50 border border-red-200 px-4 py-3 text-red-700">{passwordError}</div>}
              <form className="space-y-3" onSubmit={handlePasswordChange}>
                <div className="relative">
                  <input
                    type={passwordVisibility.currentPassword ? 'text' : 'password'}
                    value={passwordData.currentPassword}
                    onChange={(event) => setPasswordData((prev) => ({ ...prev, currentPassword: event.target.value }))}
                    className="w-full px-4 py-3 pr-20 border border-gray-300 rounded-xl"
                    placeholder="Current password"
                    required
                  />
                  <PasswordToggleButton visible={passwordVisibility.currentPassword} onClick={() => togglePasswordVisibility('currentPassword')} />
                </div>
                <div className="relative">
                  <input
                    type={passwordVisibility.newPassword ? 'text' : 'password'}
                    value={passwordData.newPassword}
                    onChange={(event) => setPasswordData((prev) => ({ ...prev, newPassword: event.target.value }))}
                    className="w-full px-4 py-3 pr-20 border border-gray-300 rounded-xl"
                    placeholder="New password"
                    minLength={8}
                    required
                  />
                  <PasswordToggleButton visible={passwordVisibility.newPassword} onClick={() => togglePasswordVisibility('newPassword')} />
                </div>
                <div className="relative">
                  <input
                    type={passwordVisibility.confirmPassword ? 'text' : 'password'}
                    value={passwordData.confirmPassword}
                    onChange={(event) => setPasswordData((prev) => ({ ...prev, confirmPassword: event.target.value }))}
                    className="w-full px-4 py-3 pr-20 border border-gray-300 rounded-xl"
                    placeholder="Confirm new password"
                    minLength={8}
                    required
                  />
                  <PasswordToggleButton visible={passwordVisibility.confirmPassword} onClick={() => togglePasswordVisibility('confirmPassword')} />
                </div>
                <button type="submit" className="btn-outline w-full" disabled={passwordSaving}>
                  {passwordSaving ? 'Changing password...' : 'Change Password'}
                </button>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CollaboratorAccount;
